<template>
    <Card>
        <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_RECONCILIATION">
            <div class="reconciliation-create">
                <div class="reconciliation-create__header">
                    <div>
                        <router-link to="/reconciliations" class="reconciliation-create__back">
                            <i class="fas fa-chevron-left" aria-hidden="true"></i> Back to reconciliations
                        </router-link>
                        <h2>Start inventory count</h2>
                        <p>
                            Count exact packages in one location and room. Submitting creates a pending reconciliation;
                            inventory does not change until upper management approves it.
                        </p>
                    </div>
                </div>

                <section class="reconciliation-create__section" aria-labelledby="count-location-heading">
                    <h3 id="count-location-heading">1. Count location</h3>
                    <div class="reconciliation-create__controls">
                        <label>
                            <span>Warehouse</span>
                            <select
                                id="reconciliation-create-warehouse"
                                v-model="selectedWarehouse"
                                class="form-control"
                                :disabled="lines.length > 0 || submitting"
                                @change="resetSearch"
                            >
                                <option value="">Select a warehouse</option>
                                <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                                    {{ warehouse.title || warehouse.name }}
                                </option>
                            </select>
                        </label>
                        <label>
                            <span>Room</span>
                            <select
                                id="reconciliation-create-room"
                                v-model="room"
                                class="form-control"
                                :disabled="lines.length > 0 || submitting"
                                @change="resetSearch"
                            >
                                <option value="front">Front Room</option>
                                <option value="back">Back Room</option>
                            </select>
                        </label>
                    </div>
                    <p v-if="lines.length" class="reconciliation-create__hint">
                        Warehouse and room are locked after the first package is added. Remove all counts to change them.
                    </p>
                </section>

                <section class="reconciliation-create__section" aria-labelledby="find-products-heading">
                    <h3 id="find-products-heading">2. Find products and packages</h3>
                    <p class="reconciliation-create__hint">
                        Scan a product barcode or enter a product name, SKU, UPC, or item code. Keyboard-wedge scanners can
                        submit the search with Enter.
                    </p>
                    <form class="reconciliation-create__search" @submit.prevent="fetchInventory">
                        <label class="sr-only" for="reconciliation-product-search">Product or barcode</label>
                        <input
                            id="reconciliation-product-search"
                            ref="searchInput"
                            v-model.trim="search"
                            class="form-control"
                            type="search"
                            autocomplete="off"
                            placeholder="Scan barcode or search products"
                            :disabled="!selectedWarehouse || loadingInventory || submitting"
                        />
                        <Button type="submit" :noMargin="true" :disabled="!canSearch">
                            {{ loadingInventory ? "Searching..." : "Find packages" }}
                        </Button>
                    </form>

                    <UiState v-if="loadingInventory" type="loading" title="Loading package inventory" compact />
                    <UiState
                        v-else-if="inventoryError"
                        type="error"
                        :message="inventoryError"
                        actionLabel="Try again"
                        compact
                        @action="fetchInventory"
                    />
                    <UiState
                        v-else-if="hasSearched && !inventory.length"
                        type="empty"
                        title="No products found"
                        message="Check the barcode or search terms and try again."
                        compact
                    />

                    <div v-else-if="inventory.length" class="reconciliation-products">
                        <article v-for="item in inventory" :key="item.id" class="reconciliation-product">
                            <header>
                                <div>
                                    <h4>{{ item.name }}</h4>
                                    <p>{{ item.item_code }}<span v-if="item.item_group"> · {{ item.item_group }}</span></p>
                                </div>
                                <span>{{ item.lots.length }} existing package{{ item.lots.length === 1 ? "" : "s" }}</span>
                            </header>

                            <div v-if="item.lots.length" class="reconciliation-product__lots">
                                <div v-for="lot in item.lots" :key="lot.ledger_id" class="reconciliation-lot-row">
                                    <div>
                                        <strong>Package {{ lot.batch_no }}</strong>
                                        <small>System stock: {{ lot.stock_qty }}</small>
                                    </div>
                                    <label>
                                        <span class="sr-only">Counted quantity for package {{ lot.batch_no }}</span>
                                        <input
                                            :value="existingQuantities[lot.ledger_id]"
                                            class="form-control"
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputmode="numeric"
                                            placeholder="Counted qty"
                                            @input="setExistingQuantity(lot.ledger_id, $event.target.value)"
                                            @keyup.enter="addExistingLot(item, lot)"
                                        />
                                    </label>
                                    <Button
                                        type="button"
                                        :noMargin="true"
                                        :disabled="submitting || hasLine(existingLineKey(lot))"
                                        @click="addExistingLot(item, lot)"
                                    >
                                        {{ hasLine(existingLineKey(lot)) ? "Added" : "Add count" }}
                                    </Button>
                                </div>
                            </div>
                            <p v-else class="reconciliation-create__hint">No existing packages were found in this room.</p>

                            <div class="reconciliation-new-lot">
                                <div>
                                    <strong>Add a new package / lot</strong>
                                    <small>Use this only when the physical package is not listed above.</small>
                                </div>
                                <label>
                                    <span class="sr-only">New package ID for {{ item.name }}</span>
                                    <input
                                        :value="newLotDrafts[item.id].batchNo"
                                        class="form-control"
                                        type="text"
                                        maxlength="80"
                                        autocomplete="off"
                                        placeholder="New package ID"
                                        @input="setNewLotDraft(item.id, 'batchNo', $event.target.value)"
                                    />
                                </label>
                                <label>
                                    <span class="sr-only">Counted quantity for new package</span>
                                    <input
                                        :value="newLotDrafts[item.id].quantity"
                                        class="form-control"
                                        type="number"
                                        min="1"
                                        step="1"
                                        inputmode="numeric"
                                        placeholder="Counted qty"
                                        @input="setNewLotDraft(item.id, 'quantity', $event.target.value)"
                                        @keyup.enter="addNewLot(item)"
                                    />
                                </label>
                                <Button type="button" :noMargin="true" :disabled="submitting" @click="addNewLot(item)">
                                    Add new package
                                </Button>
                            </div>
                        </article>
                    </div>
                </section>

                <section class="reconciliation-create__section" aria-labelledby="count-lines-heading">
                    <h3 id="count-lines-heading">3. Review package counts</h3>
                    <UiState
                        v-if="!lines.length"
                        type="empty"
                        title="No package counts added"
                        message="Search for a product, then add one or more existing or new packages."
                        compact
                    />
                    <div v-else class="reconciliation-counts">
                        <div v-for="line in lines" :key="line.key" class="reconciliation-count-line">
                            <div>
                                <strong>{{ line.name }}</strong>
                                <small>
                                    {{ line.selection_mode === "NEW_LOT" ? "New package" : "Existing package" }}
                                    {{ line.batch_no }} · system stock {{ line.stock_qty }}
                                </small>
                            </div>
                            <label>
                                <span>Counted quantity</span>
                                <input
                                    v-model="line.stock_qty_reconciled"
                                    class="form-control"
                                    type="number"
                                    :min="line.selection_mode === 'NEW_LOT' ? 1 : 0"
                                    step="1"
                                    inputmode="numeric"
                                    :disabled="submitting"
                                />
                            </label>
                            <Button type="button" variant="danger" :noMargin="true" :disabled="submitting" @click="removeLine(line.key)">
                                Remove
                            </Button>
                        </div>
                    </div>
                </section>

                <div v-if="validationError" class="reconciliation-create__error" role="alert">
                    {{ validationError }}
                </div>

                <div class="reconciliation-create__actions">
                    <router-link to="/reconciliations">
                        <Button type="button" variant="secondary" :noMargin="true" :disabled="submitting">Cancel</Button>
                    </router-link>
                    <Button type="button" :noMargin="true" :disabled="!canSubmit" @click="submitReconciliation">
                        {{ submitting ? "Submitting..." : `Submit ${lines.length} count${lines.length === 1 ? "" : "s"} for review` }}
                    </Button>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapActions, mapState } from "vuex"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import Button from "../../components/UI/Button.vue"
import Card from "../../components/UI/Card.vue"
import UiState from "../../components/UI/UiState.vue"
import { PERMISSION_CODES } from "../../store/permission"
import { http } from "../../utils/http"

const NEW_PACKAGE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,79}$/

function wholeNumber(value, minimum) {
    const normalized = String(value === undefined || value === null ? "" : value).trim()
    if (!normalized) return null
    const number = Number(normalized)
    return Number.isInteger(number) && number >= minimum ? number : null
}

function requestErrorMessage(error, fallback) {
    return error?.response?.data?.message || fallback
}

export default {
    name: "ReconciliationCreatePage",
    components: { AllowPermission, Button, Card, UiState },
    data() {
        return {
            PERMISSION_CODES,
            selectedWarehouse: "",
            room: "front",
            search: "",
            inventory: [],
            existingQuantities: {},
            newLotDrafts: {},
            lines: [],
            hasSearched: false,
            loadingInventory: false,
            inventoryError: "",
            validationError: "",
            submitting: false,
        }
    },
    computed: {
        ...mapState("misc", ["warehouses"]),
        canSearch() {
            return !!this.selectedWarehouse && !!this.search && !this.loadingInventory && !this.submitting
        },
        canSubmit() {
            return !!this.selectedWarehouse && this.lines.length > 0 && !this.submitting
        },
    },
    mounted() {
        this.setData({ pageTitle: "Start Inventory Count" })
        if (this.warehouses.length === 1) this.selectedWarehouse = this.warehouses[0].id
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        resetSearch() {
            if (this.lines.length) return
            this.inventory = []
            this.hasSearched = false
            this.inventoryError = ""
            this.validationError = ""
        },
        async fetchInventory() {
            if (!this.canSearch) return
            this.loadingInventory = true
            this.inventoryError = ""
            this.validationError = ""
            this.hasSearched = true
            try {
                const params = new URLSearchParams({
                    page: "1",
                    limit: "50",
                    search: this.search,
                    item_group: "",
                    warehouse_id: String(this.selectedWarehouse),
                    is_back_room: this.room === "back" ? "1" : "0",
                })
                const response = await http.get(`/reconciliations/inventory?${params.toString()}`)
                this.inventory = (Array.isArray(response.data?.result) ? response.data.result : []).map((item) => ({
                    ...item,
                    lots: Array.isArray(item.lots) ? item.lots : [],
                }))
                for (const item of this.inventory) {
                    if (!this.newLotDrafts[item.id]) {
                        this.$set(this.newLotDrafts, item.id, { batchNo: "", quantity: "" })
                    }
                }
            } catch (error) {
                this.inventory = []
                this.inventoryError = requestErrorMessage(error, "Could not load package inventory.")
            } finally {
                this.loadingInventory = false
            }
        },
        setExistingQuantity(ledgerId, value) {
            this.$set(this.existingQuantities, ledgerId, value)
        },
        setNewLotDraft(itemId, field, value) {
            const draft = this.newLotDrafts[itemId] || { batchNo: "", quantity: "" }
            this.$set(this.newLotDrafts, itemId, { ...draft, [field]: value })
        },
        existingLineKey(lot) {
            return `ledger:${lot.ledger_id}`
        },
        newLineKey(batchNo) {
            return `new:${String(batchNo).trim().toLowerCase()}`
        },
        hasLine(key) {
            return this.lines.some((line) => line.key === key)
        },
        addExistingLot(item, lot) {
            this.validationError = ""
            const quantity = wholeNumber(this.existingQuantities[lot.ledger_id], 0)
            if (quantity === null) {
                this.validationError = `Enter a whole counted quantity of zero or greater for package ${lot.batch_no}.`
                return
            }
            const key = this.existingLineKey(lot)
            if (this.hasLine(key)) {
                this.validationError = `Package ${lot.batch_no} is already in this reconciliation.`
                return
            }
            this.lines.push({
                key,
                item_id: item.id,
                item_code: item.item_code,
                name: item.name,
                ledger_id: lot.ledger_id,
                batch_no: lot.batch_no,
                selection_mode: "EXISTING_LOT",
                stock_qty: Number(lot.stock_qty || 0),
                stock_qty_reconciled: quantity,
            })
        },
        addNewLot(item) {
            this.validationError = ""
            const draft = this.newLotDrafts[item.id] || { batchNo: "", quantity: "" }
            const batchNo = String(draft.batchNo || "").trim()
            const quantity = wholeNumber(draft.quantity, 1)
            if (!NEW_PACKAGE_PATTERN.test(batchNo) || batchNo === "1000") {
                this.validationError = "New package IDs must use 1-80 letters, numbers, periods, underscores, slashes, or hyphens and cannot be 1000."
                return
            }
            if (quantity === null) {
                this.validationError = "A new package must have a whole counted quantity greater than zero."
                return
            }
            const key = this.newLineKey(batchNo)
            if (this.hasLine(key)) {
                this.validationError = `Package ${batchNo} is already in this reconciliation.`
                return
            }
            this.lines.push({
                key,
                item_id: item.id,
                item_code: item.item_code,
                name: item.name,
                ledger_id: null,
                batch_no: batchNo,
                selection_mode: "NEW_LOT",
                stock_qty: 0,
                stock_qty_reconciled: quantity,
            })
            this.$set(this.newLotDrafts, item.id, { batchNo: "", quantity: "" })
        },
        removeLine(key) {
            this.lines = this.lines.filter((line) => line.key !== key)
            this.validationError = ""
        },
        validateLines() {
            for (const line of this.lines) {
                const minimum = line.selection_mode === "NEW_LOT" ? 1 : 0
                const quantity = wholeNumber(line.stock_qty_reconciled, minimum)
                if (quantity === null) {
                    this.validationError = `${line.batch_no} needs a whole counted quantity of ${minimum === 1 ? "one or greater" : "zero or greater"}.`
                    return false
                }
                line.stock_qty_reconciled = quantity
            }
            return true
        },
        async submitReconciliation() {
            if (!this.canSubmit || !this.validateLines()) return
            this.submitting = true
            this.validationError = ""
            try {
                const response = await http.post("/reconciliations", {
                    reconciliation: {
                        warehouse_id: this.selectedWarehouse,
                        is_back_room: this.room === "back",
                    },
                    reconciliation_items: this.lines.map((line) => ({
                        item_id: line.item_id,
                        item_code: line.item_code,
                        ledger_id: line.ledger_id,
                        batch_no: line.batch_no,
                        selection_mode: line.selection_mode,
                        stock_qty: line.stock_qty,
                        stock_qty_reconciled: line.stock_qty_reconciled,
                    })),
                })
                this.$toast.success(response.data?.message || "Reconciliation submitted for review")
                const id = response.data?.result?.id
                await this.$router.push(id ? `/reconciliations/${id}` : "/reconciliations")
            } catch (error) {
                this.validationError = requestErrorMessage(error, "Could not submit the reconciliation.")
                this.$toast.error(this.validationError)
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.reconciliation-create {
  max-width: 1180px;
}

.reconciliation-create__header,
.reconciliation-create__section {
  margin-bottom: 24px;
}

.reconciliation-create__header h2,
.reconciliation-create__section h3,
.reconciliation-product h4 {
  margin: 0;
}

.reconciliation-create__header p,
.reconciliation-create__hint,
.reconciliation-product p,
.reconciliation-product small,
.reconciliation-count-line small,
.reconciliation-new-lot small {
  color: rgba($textColor, 0.7);
}

.reconciliation-create__back {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 12px;
}

.reconciliation-create__section {
  padding: 20px;
  border: 1px solid $lightBorderColor;
  border-radius: 10px;
  background: $lightCardBg;
}

.reconciliation-create__section h3 {
  margin-bottom: 14px;
  font-size: 1.05rem;
}

.reconciliation-create__controls,
.reconciliation-create__search {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(180px, 0.6fr);
  gap: 14px;
}

.reconciliation-create__search {
  grid-template-columns: minmax(240px, 1fr) auto;
  align-items: end;
}

.reconciliation-create__controls label,
.reconciliation-count-line label {
  display: grid;
  gap: 6px;
}

.reconciliation-create__controls label > span,
.reconciliation-count-line label > span {
  font-size: 0.82rem;
  font-weight: 700;
}

.reconciliation-products,
.reconciliation-counts {
  display: grid;
  gap: 14px;
  margin-top: 18px;
}

.reconciliation-product {
  overflow: hidden;
  border: 1px solid $lightBorderColor;
  border-radius: 8px;
}

.reconciliation-product > header,
.reconciliation-lot-row,
.reconciliation-new-lot,
.reconciliation-count-line {
  display: grid;
  align-items: center;
  gap: 12px;
  padding: 14px;
}

.reconciliation-product > header {
  grid-template-columns: 1fr auto;
  background: rgba($primaryColor, 0.06);
}

.reconciliation-product > header p,
.reconciliation-product > header h4 {
  margin-bottom: 0;
}

.reconciliation-lot-row,
.reconciliation-new-lot {
  grid-template-columns: minmax(220px, 1fr) minmax(130px, 170px) auto;
  border-top: 1px solid $lightBorderColor;
}

.reconciliation-lot-row > div,
.reconciliation-new-lot > div,
.reconciliation-count-line > div {
  display: grid;
  gap: 2px;
}

.reconciliation-new-lot {
  grid-template-columns: minmax(220px, 1fr) minmax(150px, 210px) minmax(130px, 170px) auto;
  background: rgba($warningColor, 0.06);
}

.reconciliation-count-line {
  grid-template-columns: minmax(250px, 1fr) minmax(150px, 190px) auto;
  border: 1px solid $lightBorderColor;
  border-radius: 8px;
}

.reconciliation-create__error {
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid rgba($dangerColor, 0.35);
  border-radius: 6px;
  background: rgba($dangerColor, 0.08);
  color: $dangerColor;
  font-weight: 600;
}

.reconciliation-create__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

:deep(.dark) .reconciliation-create__section,
:deep(.dark) .reconciliation-product,
:deep(.dark) .reconciliation-count-line {
  border-color: $darkBorderColor;
  background: $darkCardBg;
}

:deep(.dark) .reconciliation-create__header p,
:deep(.dark) .reconciliation-create__hint,
:deep(.dark) .reconciliation-product p,
:deep(.dark) .reconciliation-product small,
:deep(.dark) .reconciliation-count-line small,
:deep(.dark) .reconciliation-new-lot small {
  color: rgba($darkTextColor, 0.72);
}

:deep(.dark) .reconciliation-product > header {
  background: rgba($primaryColorDark, 0.1);
}

:deep(.dark) .reconciliation-lot-row,
:deep(.dark) .reconciliation-new-lot {
  border-color: $darkBorderColor;
}

:deep(.dark) .reconciliation-new-lot {
  background: rgba($warningColorDark, 0.08);
}

@media (max-width: 760px) {
  .reconciliation-create__controls,
  .reconciliation-create__search,
  .reconciliation-product > header,
  .reconciliation-lot-row,
  .reconciliation-new-lot,
  .reconciliation-count-line {
    grid-template-columns: 1fr;
  }

  .reconciliation-create__section {
    padding: 14px;
  }

  .reconciliation-create__actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .reconciliation-create__actions a,
  .reconciliation-create__actions button {
    width: 100%;
  }
}
</style>
